import { useQuery } from "@tanstack/react-query"
import {
  getListBestRatingTeachers,
  getListTeachersByBadge,
  getListUniqueClassTeachers,
} from "api/teacher.api.ts"
import HeaderBackgroundImage from "assets/hero-image.webp"
import styled from "styled-components"
import { FiltersSection } from "ui/blocks/filtersSection/FiltersSection"
import { MobileFiltersSection } from "ui/blocks/filtersSection/mobileFilterSection"
import { MainLayout } from "ui/layout/MainLayout.jsx"
import { mediaSizes, maxContainerWidth, fullWidthContainerStyles } from "ui/theme"
import { BestInstructors } from "./sections/BestInstructors"
import { Categories } from "./sections/Categories"
import { DownloadSection } from "./sections/DownloadSection"
import { HomeMapSection } from "./sections/HomeMapSection"
import { HowItWorks } from "./sections/HowItWorks"
import { PopularClasses } from "./sections/PopularClasses"
import { Testimonials } from "./sections/Testimonials"

export const HomePage = () => {
  const { data: popularClasses } = useQuery({
    queryKey: ["list-best-rating-teachers"],
    queryFn: () => getListBestRatingTeachers(30),
  })

  const { data: bestInstructors } = useQuery({
    queryKey: ["list-teachers-by-badge"],
    queryFn: () => getListTeachersByBadge({ limit: 30, badge: "top" }),
  })

  const { data: uniqueClasses } = useQuery({
    queryKey: ["list-unique-class-teachers"],
    queryFn: () => getListUniqueClassTeachers(30),
  })

  return (
    <MainLayout primary>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            Book private lessons with talented musicians near you or online
          </HeroTitle>
          <MobileFiltersSection />
          <FiltersSection />
        </HeroContent>
      </HeroSection>
      <HomeContainer>
        <Categories />
        <PopularClasses popularClasses={popularClasses?.data} />
        <HowItWorks />
        <BestInstructors bestInstructors={bestInstructors?.data} />
        <HomeMapSection />
        {/* <UniqueClasses uniqueClasses={uniqueClasses?.data} /> */}
        <Testimonials />
        <DownloadSection />
      </HomeContainer>
    </MainLayout>
  )
}

const HomeContainer = styled.div`
  
  min-height: var(--page-height);
  margin-inline: auto;

  /* Categories */
  & > :nth-child(1) {

  }

  /* PopularClasses */
  & > :nth-child(2) {

  }

  /* HowItWorks */
  & > :nth-child(3) {
    margin: 72px auto 100px;
    max-width: 1200px;
  }

  /* BestInstructors */
  & > :nth-child(4) {
 
  }

  /* HomeMapSection */
  & > :nth-child(5) {
    ${fullWidthContainerStyles}
    padding-top: 72px;

    @media (max-width: 600px) {
      padding-top: 30px;
    }
  }

  /* Testimonials */
  & > :nth-child(6) {
    ${fullWidthContainerStyles}
    margin-top: 137px;

    @media (max-width: 600px) {
      margin-top: 60px;
    }
  }

  /* DownloadSection */
  & > :nth-child(7) {
    ${fullWidthContainerStyles}
    padding-top: 50px;
    padding-bottom: 70px;

    @media (max-width: 1024px) {
      padding-top: 30px;
      padding-bottom: 50px;
    }
  }

`

const HeroSection = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url("${HeaderBackgroundImage}");
  background-size: cover;
  background-position: 50% 80%;
  background-repeat: no-repeat;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(20, 20, 20, 0.5);
    z-index: 1;
  }

  /* Phones: taller photo, clear in the middle, shade only where text sits. */
  @media (width < ${mediaSizes.md}) {
    align-items: flex-end;
    min-height: clamp(500px, 78vh, 640px);
    @supports (height: 1svh) {
      min-height: clamp(500px, 78svh, 640px);
    }
    background-position: 42% 50%;

    &::after {
      background: linear-gradient(
        180deg,
        rgba(14, 14, 18, 0.45) 0%,
        rgba(14, 14, 18, 0) 22%,
        rgba(14, 14, 18, 0) 45%,
        rgba(14, 14, 18, 0.55) 72%,
        rgba(14, 14, 18, 0.78) 100%
      );
    }
  }

  @media (width >= ${mediaSizes.md}) {
    min-height: 500px;
    height: calc(100vh - 240px);
    max-height: 630px;
    align-items: flex-end;
  }

`

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 994px;
  margin: auto;
  padding: 80px 24px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  

  @media (width < ${mediaSizes.md}) {
    margin: 0;
    padding: 96px 20px 24px;
    align-items: stretch;
    gap: 18px;
  }
`

const HeroTitle = styled.div`
  font-weight: 500;
  font-size: 30px;
  line-height: 1.2;
  text-align: center;
  color: white;
  max-width: 90%;

  @media (width < ${mediaSizes.md}) {
    max-width: 330px;
    font-size: 26px;
    font-weight: 500;
    line-height: 1.22;
    letter-spacing: -0.01em;
    text-align: left;
    text-wrap: balance;
    text-shadow: 0 1px 12px rgba(0, 0, 0, 0.25);
  }

  @media (min-width: 640px) and (width < ${mediaSizes.md}) {
    font-size: 30px;
    max-width: 420px;
  }

  @media (width >= ${mediaSizes.md}) {
    font-size: 39px;
  }
`

